import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ExpandIcon } from 'lucide-react';
import { volumePages, type VolumePage } from '../data/volumePages';
import { PageLightbox } from './PageLightbox';
export function PageCarousel() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [openPage, setOpenPage] = useState<VolumePage | null>(null);
  const last = volumePages.length - 1;
  const updateFromScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    if (max <= 0) {
      setActive(0);
      setAtStart(true);
      setAtEnd(true);
      return;
    }
    const ratio = track.scrollLeft / max;
    setActive(Math.round(ratio * last));
    setAtStart(track.scrollLeft <= 4);
    setAtEnd(track.scrollLeft >= max - 4);
  }, [last]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateFromScroll();
    track.addEventListener('scroll', updateFromScroll, {
      passive: true
    });
    window.addEventListener('resize', updateFromScroll);
    return () => {
      track.removeEventListener('scroll', updateFromScroll);
      window.removeEventListener('resize', updateFromScroll);
    };
  }, [updateFromScroll]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(last, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = !window.matchMedia('(min-width: 640px)').matches;
    const left = isMobile ? slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2 : slide.offsetLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0);
    track.scrollTo({
      left,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const closeLightbox = useCallback(() => setOpenPage(null), []);
  const arrowClass = 'hidden h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/10 lg:flex';
  return <section aria-labelledby="pages-heading" className="w-full bg-ink" data-id="element-146" data-mp="21x0pwuzeyun1">
      <div className="apparatus-strip h-1.5 w-full" aria-hidden="true" data-id="element-147" data-mp="25yc6kb0b85j3" />
      <div className="mx-auto max-w-6xl py-16 sm:py-20" data-id="element-148" data-mp="1s6xllymudu9e">
        <div className="px-5 text-center" data-id="element-149" data-mp="1s6fgsdm467pv">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-amber" data-id="element-150" data-mp="1x5ipc81ayw2l">Look inside</p>
          <h2 id="pages-heading" className="mt-3 font-display text-3xl font-extrabold text-white sm:text-5xl" data-id="element-151" data-mp="2kvmn3yrt81lo">
            Real pages from Volume 3
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/75" data-id="element-152" data-mp="2z0myjra7se9i">
            Every week comes solved in two pages: the Rotation Map runs the hour,
            the Skill Spotlight teaches the skill. Tap any page to see it full size.
          </p>
        </div>

        <div className="relative mt-10" data-id="element-153" data-mp="38sspcxoqcpn4">
          <ul ref={trackRef} aria-label="Pages from Volume 3" className="relative flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[10%] pb-2 sm:px-5 [scrollbar-width:none] sm:gap-6 [&::-webkit-scrollbar]:hidden" data-id="element-154" data-mp="33rbsdd9d9nbt">
            {volumePages.map((page, i) => <li key={page.src} className="w-[80%] flex-none snap-center text-center sm:snap-start sm:text-left sm:w-[calc((100%-24px)/2)] lg:w-[calc((100%-48px)/3)]" aria-label={`${i + 1} of ${volumePages.length}: ${page.label}`} data-id="element-155" data-mp="2q0zxx7pb7chi" data-mpi={i}>
                <button type="button" onClick={() => setOpenPage(page)} aria-label={`Open full page: ${page.label}`} className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 shadow-book focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-ink" data-id="element-156" data-mp="a3ef4hrf7md">
                  <img src={page.src} alt={page.alt} loading="lazy" width={1080} height={1920} className="aspect-[9/16] w-full object-cover" data-id="element-157" data-mp="2jwlnk6hegp4l" />
                  <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-ink/80 text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-158" data-mp="2yomb3x2dgkb6">
                    <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-159" data-mp="3cnc9rh4uas1n" />
                  </span>
                </button>
                <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-amber" data-id="element-160" data-mp="110ihvv58y8gl">
                  {page.label}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-white" data-id="element-161" data-mp="w3idrpryjfs4">{page.caption}</p>
              </li>)}
          </ul>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6 px-5" data-id="element-162" data-mp="n1ix4ykuudpp">
          <button type="button" onClick={() => goTo(active - 1)} disabled={atStart} aria-label="Previous page" className={arrowClass} data-id="element-163" data-mp="1jr9pos9l5qsv">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-164" data-mp="1axwrpbwnzyzx" />
          </button>

          <div className="flex items-center gap-2" role="group" aria-label="Choose a page" data-id="element-165" data-mp="15zlhv16ddohp">
            {volumePages.map((page, i) => <button key={page.src} type="button" onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}: ${page.label}`} aria-current={active === i ? 'true' : undefined} className="flex h-6 items-center justify-center px-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded-full" data-id="element-166" data-mp="23fiq1pla933g" data-mpi={i}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ${active === i ? 'w-6 bg-amber' : 'w-2 bg-white/30'}`} data-id="element-167" data-mp="28puwlt20y030" />
              </button>)}
          </div>

          <button type="button" onClick={() => goTo(active + 1)} disabled={atEnd} aria-label="Next page" className={arrowClass} data-id="element-168" data-mp="23u5tjfqsq11y">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-169" data-mp="1twrg7p065sdv" />
          </button>
        </div>

        <p className="mt-6 px-5 text-center text-[13px] text-white/60" data-id="element-170" data-mp="3k502f5xgcequ">
          Volume 3 of 6 · 9 weekly classes + 5 worksheets + Coach&apos;s Notes and Quick Wins in every volume
        </p>
      </div>

      <PageLightbox page={openPage} onClose={closeLightbox} data-id="element-171" data-mp="1cj016l2oro2d" />
    </section>;
}